import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Select, Input } from '../atoms';
import { EntityHeader } from '../shell';
import { DocumentViewer, useToast } from '../organisms';
import { formatDate } from '../lib/orgTimezone';
import { formatTagLabel } from '../lib/formatTag';
import { useEntityPermission } from '../lib/usePermissions';
import { useCurrentUser } from '../lib/useCurrentUser';
import { downloadDocument } from '../lib/downloadDocument';

/**
 * DocumentDetail — /v2/documents/:id
 *
 * Replaces the generic schema table for a document with a real record view: the
 * metadata PLUS an inline preview (the doc-viewer fix — you can finally VIEW a
 * document in the staff app, not just force-download it). The DocumentViewer is
 * embedded inline and fetches the bytes from the REAL staff download endpoint
 * (`/api/documents/:id/download?disposition=inline`).
 */

type ReviewStatus = 'client_submitted' | 'in_review' | 'accepted' | 'rejected' | null;

/** GET /api/document-extract/availability. */
interface ExtractAvailability {
  available: boolean;
  reason?: string;
  documentTypes?: string[];
}

/** A row of document_extractions (migration 131) — one AI read ATTEMPT. */
interface ExtractionRecord {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'unreadable' | 'unrecognized';
  classified_type: string | null;
  extracted_type: string | null;
  import_batch_id: string | null;
  rows_staged: number | null;
  error: string | null;
  created_at: string | null;
}

/** POST /api/document-extract/document/:id run result (inline, no queue backend). */
interface ExtractRunResult {
  status?: string;
  error?: string | null;
  staged?: { batchId: string; table: string; totalRecords: number };
}

interface DocumentRecord {
  id: string;
  name: string;
  filename: string;
  content_type: string | null;
  document_type: string | null;
  file_size_bytes: number | null;
  year: number | null;
  status: string | null;
  review_status: ReviewStatus;
  review_note: string | null;
  assigned_to_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

function formatBytes(v: number | null): string {
  if (v == null) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-sm)' }}>{value}</div>
    </div>
  );
}

function normalizeValues(data: string[] | { values?: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}

/**
 * 7/28 B4 — RETAG at review, from the document page itself (Christophe: a client upload
 * is untyped ("Other") until the firm types it; that retag must be doable here, not only
 * from the review queue, and must update everywhere). A staff member with document.can_edit
 * picks an existing org-settings type OR creates a new one inline; the change PUTs
 * documents.document_type and invalidates the doc caches, so the client profile + portal
 * (which now read the SAME master type — 7/28 A) reflect it immediately.
 */
function RetagControl({ doc }: { doc: DocumentRecord }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const canEdit = useEntityPermission('document', 'can_edit');
  // Creating a NEW org-settings document type is taxonomy management, gated to the two
  // roles that own it (the same rule the shared add-in-place pop-ups use). Every
  // edit-capable role can still RETAG to an EXISTING type below.
  const currentUser = useCurrentUser();
  const canAddType = currentUser?.role === 'super_admin' || currentUser?.role === 'administrator';
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState('');

  const typesQuery = useQuery<string[] | { values?: string[] }>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
    enabled: canEdit,
  });
  const types = normalizeValues(typesQuery.data);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['documents-list'] });
    qc.invalidateQueries({ queryKey: ['documents-view-status'] });
    qc.invalidateQueries({ queryKey: ['org-settings', 'document-types'] });
  }

  const retag = useMutation<unknown, Error, string>({
    mutationFn: (type) => api.put(`/api/documents/${doc.id}`, { document_type: type }),
    onSuccess: () => { invalidate(); toast({ variant: 'success', message: 'Document retagged.' }); },
    onError: () => toast({ variant: 'error', message: 'Could not retag the document.' }),
  });

  // Create a new type in the master list, then apply it. Uses the single-option
  // add-in-place endpoint (idempotent, super_admin + administrator) — the SAME one the
  // shared taxonomy pop-ups use — NOT the full-registry PUT (which is super_admin-only
  // and 403'd every other role, taking the chained retag down with it).
  const addAndApply = useMutation<unknown, Error, string>({
    mutationFn: async (type) => {
      await api.post('/api/org-settings/document-types/add-option', { value: type });
      await api.put(`/api/documents/${doc.id}`, { document_type: type });
    },
    onSuccess: () => { invalidate(); setAdding(false); setNewType(''); toast({ variant: 'success', message: 'Type created + applied.' }); },
    onError: () => toast({ variant: 'error', message: 'Could not create the type.' }),
  });

  const busy = retag.isPending || addAndApply.isPending;

  if (!canEdit) {
    return doc.document_type ? <Badge variant="neutral">{formatTagLabel(doc.document_type)}</Badge> : <Badge variant="neutral">Other</Badge>;
  }

  if (adding) {
    const trimmed = newType.trim();
    const dup = types.some((t) => t.toLowerCase() === trimmed.toLowerCase());
    return (
      <div style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center', flexWrap: 'wrap' }}>
        <Input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="New type" autoFocus style={{ maxWidth: 160 }} />
        <Button size="sm" variant="primary" loading={addAndApply.isPending} disabled={!trimmed || dup || busy} onClick={() => addAndApply.mutate(trimmed)}>Add</Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => { setAdding(false); setNewType(''); }}>Cancel</Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 150 }}>
        <Select
          value={doc.document_type ?? ''}
          disabled={busy}
          onChange={(e) => { if (e.target.value) retag.mutate(e.target.value); }}
          placeholder="Untyped (Other)"
          options={types.map((t) => ({ value: t, label: formatTagLabel(t) }))}
        />
      </div>
      {canAddType && <Button size="sm" variant="secondary" disabled={busy} onClick={() => setAdding(true)}>+ New</Button>}
    </div>
  );
}

/**
 * 7/28 C4.1 — review a client upload FROM the document page. Christophe: a client
 * upload notification opens the document, but the push/accept action lived only on the
 * review queue — you had to navigate back. When the doc is awaiting review, a staffer
 * with document.can_edit can Accept (push) or Reject (with a note) right here; the
 * decision PATCHes /api/documents/:id/review and refreshes the doc + the review queue.
 */
function ReviewControl({ doc }: { doc: DocumentRecord }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const canEdit = useEntityPermission('document', 'can_edit');
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['documents', doc.id] });
    qc.invalidateQueries({ queryKey: ['documents-list'] });
    qc.invalidateQueries({ queryKey: ['documents-review-queue'] });
    qc.invalidateQueries({ queryKey: ['documents-view-status'] });
  }

  const review = useMutation<unknown, Error, { review_status: 'accepted' | 'rejected'; review_note?: string }>({
    mutationFn: (body) => api.patch(`/api/documents/${doc.id}/review`, body),
    onSuccess: (_r, vars) => {
      invalidate();
      setRejecting(false); setNote('');
      toast({ variant: 'success', message: vars.review_status === 'accepted' ? 'Document accepted.' : 'Document rejected — the client was notified.' });
    },
    onError: () => toast({ variant: 'error', message: 'Could not update the review.' }),
  });

  const pending = doc.review_status === 'client_submitted' || doc.review_status === 'in_review';
  if (!pending || !canEdit) return null;
  const busy = review.isPending;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap',
      padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)',
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderLeft: '3px solid var(--brand-primary)', borderRadius: 'var(--r-lg)',
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          {doc.review_status === 'in_review' ? 'In review' : 'Awaiting review'}
        </div>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginTop: 2 }}>
          This client upload needs a decision — accept it (push it through) or reject it with a reason. No need to open the review queue.
        </div>
      </div>
      {rejecting ? (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap', flex: 1, minWidth: 220 }}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (shared with the client)" autoFocus style={{ flex: 1, minWidth: 160 }} />
          <Button size="sm" variant="danger" loading={busy} onClick={() => review.mutate({ review_status: 'rejected', review_note: note.trim() || undefined })}>Send rejection</Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => { setRejecting(false); setNote(''); }}>Cancel</Button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexShrink: 0 }}>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setRejecting(true)}>Reject</Button>
          <Button size="sm" variant="primary" loading={busy} onClick={() => review.mutate({ review_status: 'accepted' })}>Accept</Button>
        </div>
      )}
    </div>
  );
}

interface StaffUser { id: string; email: string; role: string; first_name: string | null; last_name: string | null; }
const NON_STAFF_ROLES = new Set(['client', 'consultant']);
const staffLabel = (u: StaffUser) => [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email;

/**
 * Assign / route a document to a staff reviewer FROM the document page (Christophe:
 * assigning lived only on the Documents Review Queue — you had to navigate back).
 * A staffer with document.can_edit picks an assignee here; PATCHes /api/documents/:id/assign
 * and refreshes the doc + the review queue + the list (same endpoint the queue uses).
 */
function AssignControl({ doc }: { doc: DocumentRecord }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const canEdit = useEntityPermission('document', 'can_edit');
  const staffQuery = useQuery<StaffUser[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<StaffUser[]>('/api/users-list', signal),
    staleTime: 5 * 60 * 1000,
  });
  const staff = (staffQuery.data ?? []).filter((u) => !NON_STAFF_ROLES.has(u.role));

  const assign = useMutation<unknown, Error, string | null>({
    mutationFn: (userId) => api.patch(`/api/documents/${doc.id}/assign`, { assigned_to_user_id: userId }),
    onSuccess: (_r, userId) => {
      qc.invalidateQueries({ queryKey: ['documents', doc.id] });
      qc.invalidateQueries({ queryKey: ['documents-list'] });
      qc.invalidateQueries({ queryKey: ['documents-review-queue'] });
      toast({ variant: 'success', message: userId ? 'Document assigned.' : 'Document unassigned.' });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error) : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  if (!canEdit) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Assigned to</span>
      <div style={{ minWidth: 220 }}>
        <Select
          value={doc.assigned_to_user_id ?? ''}
          disabled={assign.isPending || staffQuery.isLoading}
          onChange={(e) => assign.mutate(e.target.value || null)}
          placeholder="— Unassigned —"
          options={staff.map((u) => ({ value: u.id, label: staffLabel(u) }))}
        />
      </div>
    </div>
  );
}

/**
 * ReadControl — the second door onto document ingestion (the first is the source step in
 * ImportWizard). Reads THIS document into a staged batch and links to the review.
 *
 * Deliberately sits BESIDE the existing surfaces, changing none of them: typing the
 * figures in by hand off the inline viewer works exactly as before, and this is an offer,
 * not a replacement. When AI reading isn't available the panel explains why instead of
 * disappearing — a firm that has it switched off should know the capability exists.
 *
 * Nothing is written to a business table here: the read produces staged rows a human
 * accepts in the import review, the same gate a spreadsheet's rows pass through.
 */
function ReadControl({ doc }: { doc: DocumentRecord }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  // Reading a document into staged rows IS an import, so it is gated on the import
  // grant — not on document.can_edit, which would be the wrong capability.
  const canImport = useEntityPermission('import_batch', 'can_add');

  const availabilityQuery = useQuery<ExtractAvailability>({
    queryKey: ['document-extract-availability'],
    queryFn: ({ signal }) => api.get<ExtractAvailability>('/api/document-extract/availability', signal),
    enabled: canImport,
    retry: false,
  });

  const historyQuery = useQuery<{ extractions: ExtractionRecord[] }>({
    queryKey: ['document-extractions', doc.id],
    queryFn: ({ signal }) => api.get<{ extractions: ExtractionRecord[] }>(`/api/document-extract/document/${doc.id}`, signal),
    enabled: canImport,
    retry: false,
  });

  const read = useMutation<ExtractRunResult, Error, void>({
    mutationFn: () => api.post<ExtractRunResult>(`/api/document-extract/document/${doc.id}`, {}),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['document-extractions', doc.id] });
      if (res.staged?.batchId) {
        toast({
          variant: 'success',
          message: `Read ${res.staged.totalRecords} row${res.staged.totalRecords === 1 ? '' : 's'} — review them before committing.`,
        });
        navigate('/imports');
        return;
      }
      // Read fine, staged nothing — an answer, not an error. The reason is the payload.
      toast({ variant: 'warning', message: res.error || 'Nothing could be staged from this document.' });
    },
    onError: (err) => toast({ variant: 'error', message: err.message || 'Could not read this document.' }),
  });

  if (!canImport) return null;

  const available = !!availabilityQuery.data?.available;
  const latest = historyQuery.data?.extractions?.[0];
  const suggestedType = latest?.classified_type;
  // Only worth surfacing when it disagrees with how the document is filed — that is the
  // case where a human should act on it. Applying it stays a manual retag: document_type
  // is the key the money-blind download gate reads, so extraction never writes it.
  const showSuggestion = suggestedType && suggestedType !== doc.document_type;

  return (
    <div style={{
      padding: 'var(--sp-4)', marginBottom: 'var(--sp-4)',
      background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)',
    }}>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>Read this document</span>
        <Button
          size="sm"
          variant="secondary"
          loading={read.isPending}
          disabled={!available || read.isPending}
          onClick={() => read.mutate()}
        >
          {read.isPending ? 'Reading…' : 'Read into a staged import'}
        </Button>
        {latest && (
          <Badge variant={latest.status === 'succeeded' ? 'success' : latest.status === 'failed' ? 'danger' : 'neutral'} dot>
            last read: {formatTagLabel(latest.status)}
          </Badge>
        )}
      </div>

      <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        {available
          ? 'Pulls the figures off this document into a staged import you review before anything is written. Typing them in by hand still works exactly as before.'
          : (availabilityQuery.data?.reason || 'AI document reading is not available on this system.')}
      </p>

      {latest?.error && latest.status !== 'succeeded' && (
        <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          {latest.error}
        </p>
      )}

      {showSuggestion && (
        <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          This reads like a <strong>{formatTagLabel(suggestedType)}</strong>
          {doc.document_type ? <> rather than a {formatTagLabel(doc.document_type)}</> : null}. Retag it above if that's right.
        </p>
      )}

      {latest?.import_batch_id && latest.status === 'succeeded' && (
        <p style={{ margin: 'var(--sp-2) 0 0' }}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/imports')}>
            Go to the staged import
          </Button>
        </p>
      )}
    </div>
  );
}

export function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const docQuery = useQuery<DocumentRecord>({
    queryKey: ['documents', id],
    queryFn: ({ signal }) => api.get<DocumentRecord>(`/api/documents/${id}`, signal),
    enabled: !!id,
  });

  if (docQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading document…</p>;
  }
  if (docQuery.error) {
    const notFound = docQuery.error instanceof ApiClientError && docQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Document not found' : 'Failed to load document'}</h2>
        <p style={{ margin: 0 }}>{(docQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/documents')}>Back to Documents</Button>
        </p>
      </div>
    );
  }
  if (!docQuery.data) return null;

  const doc = docQuery.data;
  const downloadBase = `/api/documents/${doc.id}/download`;

  return (
    <>
      <EntityHeader
        prefix="Document"
        title={doc.name || doc.filename}
        subtitle={doc.filename}
        statusPills={doc.status ? <Badge variant="neutral" dot>{doc.status}</Badge> : undefined}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/documents')}>Back</Button>
            <Button variant="primary" size="sm" onClick={() => downloadDocument(doc.id, (m) => toast({ variant: 'error', message: m }))}>Download</Button>
          </>
        }
      />

      <ReviewControl doc={doc} />
      <AssignControl doc={doc} />
      <ReadControl doc={doc} />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 'var(--sp-4)',
        padding: 'var(--sp-4)', marginBottom: 'var(--sp-5)',
        background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)',
      }}>
        <Field label="Type" value={<RetagControl doc={doc} />} />
        <Field label="Content type" value={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{doc.content_type || '—'}</span>} />
        <Field label="Size" value={formatBytes(doc.file_size_bytes)} />
        <Field label="Year" value={doc.year ?? '—'} />
        <Field label="Uploaded" value={formatDate(doc.created_at, { dateOnly: true })} />
        <Field label="Updated" value={formatDate(doc.updated_at, { dateOnly: true })} />
      </div>

      <DocumentViewer
        id={doc.id}
        name={doc.name}
        filename={doc.filename}
        content_type={doc.content_type}
        downloadBase={downloadBase}
      />
    </>
  );
}
